'use client';

/**
 * Choose page for the auth flow (buyer or supplier)
 * Shows the logo and the two applications (buyer and supplier)
 * The user can choose the application they want to continue with
 * The user is redirected to the login page of the chosen application
 * The user can also go back to the home page of Andercore
 */

import Image from 'next/image';
import Link from 'next/link';
import { use } from 'react';
import Logo from '~/components/core/Logo/Logo';
import Flex from '~/components/customComponents/flex/Flex';
import Text from '~/components/customComponents/texts/Text';
import { AndercoreExternalLinks } from '~/constants/endpoints';
import { useI18n } from '~/locales/client';
import { LocaleEnum } from '~/locales/resources';

type ChoosePageProps = {
  params: Promise<{ locale?: string }>;
};

const ChoosePage = ({ params }: ChoosePageProps) => {
  const translate = useI18n();
  const unwrappedParams = use(params);
  const locale = unwrappedParams?.locale ?? 'en';

  const buyerAppLoginUrl = AndercoreExternalLinks[locale as LocaleEnum]?.buyerLogin;
  const supplierAppLoginUrl = AndercoreExternalLinks[locale as LocaleEnum]?.supplierLogin;
  const andercoreUrl = AndercoreExternalLinks[locale as LocaleEnum]?.andercoreHome;

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-gradient-to-br from-black via-neutral-900 to-red-950 text-white flex flex-col">
      {/* Secondary overlay gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-red-900/20 via-transparent to-transparent" />

      {/* Grid overlay */}
      <div
        className="absolute inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      {/* Header / Logo */}
      <div className="relative z-10 p-6 md:p-8 inline-flex items-center hover:opacity-80 transition-opacity cursor-pointer">
        <Logo logoColor="white" href={andercoreUrl} />
      </div>

      {/* Content - Vertically Centered */}
      <Flex className="relative z-10 flex-col items-center justify-center flex-1 px-6 pb-16">
        <Text className="text-3xl md:text-5xl font-bold text-center text-white">
          {translate('general.welcome-back')}
        </Text>
        <Text className="text-sm md:text-base text-white/60 mt-3 text-center">
          {translate('general.choose-application-to-continue')}
        </Text>

        <Flex className="mt-6 md:mt-15 w-full max-w-5xl flex-col md:flex-row gap-6">
          {/* Buyer card */}
          <Link
            href={buyerAppLoginUrl}
            prefetch={false}
            className="group flex-1 rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8 transition-all duration-200 hover:scale-[1.02] hover:bg-white/10 hover:border-white/20 flex flex-col gap-6"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.3)' }}
          >
            <Flex className="flex-col">
              <Flex className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-red-500/90 items-center justify-center">
                <Image
                  src="/images/logo_small_white.png"
                  alt={translate('general.buyer-app-title')}
                  width={100}
                  height={100}
                  priority
                />
              </Flex>

              <Text className="mt-5 text-lg md:text-2xl font-semibold text-white">
                {translate('general.buyer-app-title')}
              </Text>
              <Text className="mt-2 text-sm md:text-base text-white/50">
                {translate('general.buyer-app-description')}
              </Text>
            </Flex>
            <Text className="mt-auto text-sm md:text-base font-medium transition-colors text-red-400 group-hover:text-red-300">
              {translate('general.continue-to-login')} →
            </Text>
          </Link>

          {/* Supplier card */}
          <Link
            href={supplierAppLoginUrl}
            prefetch={false}
            className="group flex-1 rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8 transition-all duration-200 hover:scale-[1.02] hover:bg-white/10 hover:border-white/20 flex flex-col gap-6"
            style={{ boxShadow: '0 0 40px rgba(239, 68, 68, 0.3)' }}
          >
            <Flex className="flex-col">
              <Flex className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-red-500/90 items-center justify-center">
                <Image
                  src="/images/logo_small_black.png"
                  alt={translate('general.supplier-app-title')}
                  width={100}
                  height={100}
                  priority
                />
              </Flex>
              <Text className="mt-5 text-lg md:text-2xl font-semibold text-white">
                {translate('general.supplier-app-title')}
              </Text>
              <Text className="mt-2 text-sm md:text-base text-white/50">
                {translate('general.supplier-app-description')}
              </Text>
            </Flex>
            <Text className="mt-auto text-sm md:text-base font-medium transition-colors text-red-400 group-hover:text-red-300">
              {translate('general.continue-to-login')} →
            </Text>
          </Link>
        </Flex>
      </Flex>
    </div>
  );
};

export default ChoosePage;
